import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

export const SURVEY_DAYS = [30, 60, 90];

const multiSelectIntFilter = (label, options) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={String(o)}>{o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const boolFilter = (label, t) => ({
  filter: true, sort: false, filterType: 'custom',
  filterOptions: {
    defaultOp: 'eq',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0] !== undefined ? filterList[index][0] : '';
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              filterList[index] = v === '' ? [] : [v];
              onChange(filterList[index], index, column);
            }}
          >
            <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
            <MenuItem value="true">{t('common.yes')}</MenuItem>
            <MenuItem value="false">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>
      );
    },
  },
});

const useObSurveysApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/onboarding-surveys?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Form value ↔ API value. Day is an int (one of SURVEY_DAYS); QuestionsJson is
  // a raw JSON string the editor edits as text. An empty/blank box becomes "[]".
  const toPayload = useCallback((formData) => ({
    name: formData.name,
    day: Number(formData.day) || 0,
    questionsJson: (formData.questionsJson || '').trim() || '[]',
  }), []);

  const toFormValues = useCallback((row) => ({
    id: row.id,
    name: row.name,
    day: row.day,
    questionsJson: row.questionsJson || '[]',
  }), []);

  const create = useCallback(async (formData) => {
    try { await api.post('/v1/onboarding-surveys', toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const update = useCallback(async (id, formData) => {
    try { await api.put(`/v1/onboarding-surveys/${id}`, toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/onboarding-surveys/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('onboardingSurvey.name'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'day',
      label: t('onboardingSurvey.day'),
      options: { ...multiSelectIntFilter(t('onboardingSurvey.day'), SURVEY_DAYS), customBodyRender: (v) => `${v}` },
    },
    numericFilterColumn('questionCount', t('onboardingSurvey.questionCount'), { decimals: 0 }),
    {
      name: 'isActive',
      label: t('onboardingSurvey.isActive'),
      options: { ...boolFilter(t('onboardingSurvey.isActive'), t), customBodyRender: (v) => v ? '✓' : '-' },
    },
    dateFilterColumn('createDate', t('onboardingSurvey.createDate'), lang),
  ], [t, lang]);

  const objectSchema = useMemo(() => ([
    { name: 'name', label: t('onboardingSurvey.name'), type: 'text', required: true },
    {
      name: 'day', label: t('onboardingSurvey.day'), type: 'select', required: true, grid: 6,
      description: t('onboardingSurvey.form.dayHint'),
      options: SURVEY_DAYS.map((d) => ({ id: d, name: String(d) })),
    },
    {
      name: 'questionsJson', label: t('onboardingSurvey.form.questions'), type: 'textarea', rows: 8,
      description: t('onboardingSurvey.form.questionsHint'),
    },
  ]), [t]);

  return { fetching, data, pagination, columns, objectSchema, toFormValues, refresh, create, update, remove };
};

export default useObSurveysApi;
